import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

type ThemeMode = "light" | "dark" | "system";

interface ThemeState {
  mode: ThemeMode;
  resolved: "light" | "dark";
  setMode: (mode: ThemeMode) => void;
  a11yLarge: boolean;
  setA11yLarge: (value: boolean) => void;
}

const ThemeContext = createContext<ThemeState | null>(null);

function readStoredMode(): ThemeMode {
  try {
    const stored = localStorage.getItem("sbd_theme_mode");
    if (stored === "light" || stored === "dark" || stored === "system") return stored;
  } catch {
    /* localStorage unavailable (private mode) — fall back to system. */
  }
  return "system";
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<ThemeMode>(readStoredMode);
  const [a11yLarge, setA11yLargeState] = useState<boolean>(() => {
    try {
      return localStorage.getItem("sbd_a11y_large") === "true";
    } catch {
      return false;
    }
  });
  const [systemDark, setSystemDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const listener = (e: MediaQueryListEvent) => setSystemDark(e.matches);
    mq.addEventListener("change", listener);
    return () => mq.removeEventListener("change", listener);
  }, []);

  const resolved: "light" | "dark" = mode === "system" ? (systemDark ? "dark" : "light") : mode;

  useEffect(() => {
    document.documentElement.classList.toggle("dark", resolved === "dark");
  }, [resolved]);

  useEffect(() => {
    document.documentElement.classList.toggle("a11y-large", a11yLarge);
  }, [a11yLarge]);

  function setMode(next: ThemeMode) {
    setModeState(next);
    try {
      localStorage.setItem("sbd_theme_mode", next);
    } catch {
      /* ignore */
    }
  }

  function setA11yLarge(value: boolean) {
    setA11yLargeState(value);
    try {
      localStorage.setItem("sbd_a11y_large", String(value));
    } catch {
      /* ignore */
    }
  }

  return <ThemeContext.Provider value={{ mode, resolved, setMode, a11yLarge, setA11yLarge }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within a ThemeProvider.");
  return ctx;
}
